/**
 * Endpoints du domaine wallet (wallet-service) — source canonique.
 * Chemins copiés TELS QUELS de la collection Postman / du guide §6.3
 * (préfixe gateway `/gw/wallet`). Ne jamais inventer un chemin.
 * `@/core/api/endpoints` ré-exporte ces valeurs : ajouter les routes ICI.
 */
export const walletEndpoints = {
  wallets: {
    /** POST — authentifié. Naît en PENDING_KYC / TIER0 (étape 3 du parcours d'inscription). */
    add: "/gw/wallet/api/v1/Wallets/add",
    /** POST — liste paginée, corps `ListRequest` ({ filter, page, pageSize }). */
    all: "/gw/wallet/api/v1/Wallets/all",
    /** GET — détail d'un portefeuille. */
    detail: (walletId: string) => `/gw/wallet/api/v1/Wallets/${walletId}`,
    /** GET — solde (montants `*Minor`, unité mineure). */
    balance: (walletId: string) => `/gw/wallet/api/v1/Wallets/${walletId}/balance`,
    /** POST — back-office. Paramètre query `status` (enum majuscules). */
    status: (walletId: string) => `/gw/wallet/api/v1/Wallets/${walletId}/status`,
    /**
     * POST — flashpay-service, flashpay-superadmin, flashpay-admin.
     * Étape 6 du parcours : normalement déclenché par l'événement
     * `kyc.case.approved` (Kafka non câblé) — appel HTTP explicite.
     * Paramètre query `kycLevel`.
     */
    kycApproval: (walletId: string) =>
      `/gw/wallet/api/v1/Wallets/${walletId}/kyc-approval`,
  },
  transactions: {
    /** POST — idempotence OBLIGATOIRE (en-tête Idempotency-Key, nouvelle clé par tentative métier). */
    initiate: "/gw/wallet/api/v1/Transactions/initiate",
    /** POST — historique paginé, corps `ListRequest` ({ filter, page, pageSize }). */
    all: "/gw/wallet/api/v1/Transactions/all",
    /** GET — détail / reçu d'une transaction. */
    detail: (transactionId: string) =>
      `/gw/wallet/api/v1/Transactions/${transactionId}`,
    /**
     * POST — orchestration back-office (Kafka non câblé, appel HTTP explicite,
     * compte flashpay-service/back-office).
     */
    confirmLedger: (transactionId: string) =>
      `/gw/wallet/api/v1/Transactions/${transactionId}/confirm-ledger`,
    /** POST — orchestration back-office. Paramètre query `reason`. */
    markFailed: (transactionId: string) =>
      `/gw/wallet/api/v1/Transactions/${transactionId}/mark-failed`,
    /**
     * POST — transfert par lecture d'un QR client. Idempotence obligatoire.
     * Authentifié, pas de rôle particulier.
     */
    scanTransfer: "/gw/wallet/api/v1/Transactions/scan-transfer",
  },
  /**
   * Émission / retrait de monnaie électronique (guide v2.0 §5.5 / §6.3).
   * issue / redeem : Idempotency-Key obligatoire.
   */
  emoneyIssuances: {
    /** POST — flashpay-superadmin, flashpay-admin + demande APPROVED. */
    issue: "/gw/wallet/api/v1/EmoneyIssuances/issue",
    /** POST — flashpay-superadmin, flashpay-admin + demande APPROVED. */
    redeem: "/gw/wallet/api/v1/EmoneyIssuances/redeem",
    /** GET — flashpay-superadmin, flashpay-admin. Query `currency` optionnelle. */
    reconciliation: "/gw/wallet/api/v1/EmoneyIssuances/reconciliation",
  },
  walletQrCodes: {
    /** GET — authentifié, pas de rôle particulier. Query `walletId` optionnelle. */
    me: "/gw/wallet/api/v1/WalletQrCodes/me",
    /** GET — authentifié. Jeton opaque (encoder le path). */
    resolve: (token: string) =>
      `/gw/wallet/api/v1/WalletQrCodes/resolve/${encodeURIComponent(token)}`,
  },
  beneficiaries: {
    /** POST — ajout d'un bénéficiaire. */
    add: "/gw/wallet/api/v1/Beneficiaries/add",
    /** GET — bénéficiaires d'un portefeuille. */
    byWallet: (walletId: string) =>
      `/gw/wallet/api/v1/Beneficiaries/wallet/${walletId}`,
    /** DELETE — suppression d'un bénéficiaire. */
    remove: (beneficiaryId: string) =>
      `/gw/wallet/api/v1/Beneficiaries/${beneficiaryId}`,
  },
  /**
   * Relevé d'opérations PDF expédié par notification-service (guide v2.3 §6.7
   * / §6.3). Ouvert à tout titulaire — particulier, marchand, agent, entreprise
   * — sur ses propres portefeuilles.
   */
  statements: {
    /** POST — authentifié. `walletId` obligatoire pour un compte multi-portefeuilles. */
    request: "/gw/wallet/api/v1/Statements/request",
    /**
     * GET — guide v3.1. Télécharge le PDF d'un relevé déjà demandé (recomposé
     * à la demande, jamais conservé côté serveur). `application/pdf`.
     * Authentifié — relevés de ses propres portefeuilles ; vue complète :
     * flashpay-superadmin, flashpay-admin, flashpay-service. 404 hors périmètre.
     */
    download: (statementId: string) =>
      `/gw/wallet/api/v1/Statements/requests/${statementId}/download`,
  },
  /**
   * Codes de retrait Non-Member (guide v2.4 §6.3). Encaissement chez un
   * agent de terrain : vérification sans effet → preview → redeem idempotent.
   */
  nonMemberCodes: {
    /** POST — vérification code+téléphone (sans effet financier). 5 échecs = blocage définitif. */
    verify: "/gw/wallet/api/v1/NonMemberCodes/verify",
    /** POST — idempotence OBLIGATOIRE. Transaction AGENT_CASH_OUT + commission agent. */
    redeem: "/gw/wallet/api/v1/NonMemberCodes/redeem",
  },
  /**
   * Extensions wallet guide v2.4 et v2.5 : annulation Non-Member, workers par
   * référence fonctionnelle (voies principales ledger-service), apply-kyc par
   * party (voie principale kyc-service), QR agent statique, résolution par tel.
   */
  walletsExt: {
    /** POST — guide v2.4. Résolution portefeuille principal par numéro E.164. */
    resolveByPhone: "/gw/wallet/api/v1/Wallets/resolve-by-phone",
    /**
     * POST — guide v2.5. Voie PRINCIPALE du worker kyc-service : applique le
     * palier KYC à TOUS les portefeuilles du titulaire + migration
     * Non-Member → membre. Dédupliqué par eventId.
     * Rôles : flashpay-service, flashpay-superadmin.
     */
    applyKycApproval: "/gw/wallet/api/v1/Wallets/apply-kyc-approval",
  },
  transactionsExt: {
    /** POST — guide v2.4. Annule un transfert vers Non-Member non encaissé. */
    cancel: (transactionId: string) =>
      `/gw/wallet/api/v1/Transactions/${transactionId}/cancel`,
    /** POST — guide v2.5. Voie PRINCIPALE worker ledger-service. */
    confirmLedgerByReference:
      "/gw/wallet/api/v1/Transactions/confirm-ledger-by-reference",
    /** POST — guide v2.5. Symétrique : répercute un rejet comptable par référence. */
    markFailedByReference:
      "/gw/wallet/api/v1/Transactions/mark-failed-by-reference",
    /** POST — guide v2.5. Répercute une contre-passation comptable validée en maker-checker. */
    reverseByReference:
      "/gw/wallet/api/v1/Transactions/reverse-by-reference",
  },
  walletQrCodesExt: {
    /**
     * GET — guide v2.4. QR agent **statique** (préfixe jeton `A1.`, non tournant).
     * Rôles : flashpay-agents. Pour dépôt espèces AGENT_CASH_IN.
     */
    agentMe: "/gw/wallet/api/v1/WalletQrCodes/agent/me",
  },
  /**
   * Contestations de transfert (guide v3.1 — TransactionDisputes). L'expéditeur
   * d'un transfert COMPLETED conteste ≤ 1 h après l'envoi
   * (`Disputes__OpeningWindowMinutes`) ; le montant est gelé chez le
   * bénéficiaire (`heldAmountMinor`) dans la limite de son disponible, pendant
   * 72 h max (`Disputes__HoldDurationHours`) — sans décision, le gel est levé
   * et la contestation close comme rejetée.
   */
  transactionDisputes: {
    /**
     * POST — idempotence OBLIGATOIRE. Expéditeur uniquement, dans la fenêtre
     * d'1 h. Corps `{ transactionId, reason }` (reason : 5 à 500 caractères).
     * 201 ouverte ; 404 inconnue / pas l'expéditeur ; 422 non contestable.
     */
    open: "/gw/wallet/api/v1/TransactionDisputes/open",
    /**
     * POST — file de travail paginée, corps `ListRequest` ({ filter, page,
     * pageSize }) ; filtre `{ status?: OPEN|REVERSED|REJECTED, transactionId? }`.
     * Client : ses propres ouvertures ; vue complète : flashpay-superadmin,
     * flashpay-admin, flashpay-service.
     */
    all: "/gw/wallet/api/v1/TransactionDisputes/all",
    /** GET — détail. 404 hors périmètre (sauf back-office). */
    detail: (transactionDisputeId: string) =>
      `/gw/wallet/api/v1/TransactionDisputes/${transactionDisputeId}`,
    /**
     * POST — idempotence OBLIGATOIRE. flashpay-superadmin, flashpay-admin.
     * Rejette : lève le gel, aucune circulation de fonds → pas de maker-checker
     * (décision serveur). Corps `{ note }` (5 à 500 caractères). 422 si déjà
     * close. La restitution des fonds passe par
     * `transactionsExt.reverseByReference` (contre-passation maker-checker).
     */
    reject: (transactionDisputeId: string) =>
      `/gw/wallet/api/v1/TransactionDisputes/${transactionDisputeId}/reject`,
  },
  /**
   * Portefeuilles-coffres d'épargne (guide v3.1 — SavingsVaultWallets).
   * Ressource **INTERNE** du wallet-service, réservée au rôle
   * `flashpay-service` : c'est savings-service qui l'appelle. Une application
   * n'appelle JAMAIS ces routes — elle passe par `/gw/savings`.
   * Déclarées pour parité documentaire uniquement : aucun hook, aucun écran.
   */
  savingsVaultWallets: {
    /** INTERNAL — flashpay-service only, do not call from the app */
    add: "/gw/wallet/api/v1/SavingsVaultWallets",
    /** INTERNAL — flashpay-service only, do not call from the app */
    remove: (savingsVaultWalletId: string) =>
      `/gw/wallet/api/v1/SavingsVaultWallets/${savingsVaultWalletId}`,
    /** INTERNAL — flashpay-service only, do not call from the app */
    deletable: "/gw/wallet/api/v1/SavingsVaultWallets/deletable",
    /** INTERNAL — flashpay-service only, do not call from the app */
    balances: "/gw/wallet/api/v1/SavingsVaultWallets/balances",
    /** INTERNAL — flashpay-service only, do not call from the app */
    transfers: "/gw/wallet/api/v1/SavingsVaultWallets/transfers",
    /** INTERNAL — flashpay-service only, do not call from the app */
    lock: (savingsVaultWalletId: string) =>
      `/gw/wallet/api/v1/SavingsVaultWallets/${savingsVaultWalletId}/lock`,
    /** INTERNAL — flashpay-service only, do not call from the app */
    close: (savingsVaultWalletId: string) =>
      `/gw/wallet/api/v1/SavingsVaultWallets/${savingsVaultWalletId}/close`,
  },
  /**
   * Ressource wallet-service exposée à `flashpay-service` **uniquement** (v4.1
   * §6.3) : payroll-service l'appelle pour exécuter une paie. Déclarée ici pour
   * parité documentaire — l'application n'appelle **jamais** ces endpoints,
   * elle passe par `/gw/payroll/api/v1/PayrollBatches/*`. Idempotence
   * obligatoire sur `POST /PayrollDisbursements` (§4.7 v4.1, scope
   * `disbursementId`).
   */
  payrollDisbursements: {
    /** INTERNAL — flashpay-service only, do not call from the app */
    create: "/gw/wallet/api/v1/PayrollDisbursements",
    /** INTERNAL — flashpay-service only, do not call from the app */
    preview: "/gw/wallet/api/v1/PayrollDisbursements/preview",
    /** INTERNAL — flashpay-service only, do not call from the app */
    detail: (payrollDisbursementId: string) =>
      `/gw/wallet/api/v1/PayrollDisbursements/${payrollDisbursementId}`,
  },
} as const;
